// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ImageCommentPin.test.tsx
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { createContext } from "react";

/**
 * Which reject grays a pin: one made in this review (`liked === false`), or
 * one from an earlier review of the same artwork (`previouslyRejected`, or
 * the earlier comment's own `disposition`).
 * The two get different marks and names, as the two cards do.
 */
export type RejectedPinKind = "here" | "before";

/**
 * Names a pin whose finding was rejected, in this review or an earlier one,
 * for a reader.
 *
 * Set only by the Ask v2 artwork viewer, which draws a rejected finding's pin
 * grayed in its place. With no provider (every v1 surface) a pin renders
 * exactly as it always has, whatever `liked` or `previouslyRejected` says.
 *
 * why a context and not a prop: the pins render inside the image and PDF
 * viewers nested in `AssetViewerPane`, a 5k-line v1 monolith; a prop would
 * thread through each of them for a v2-only look.
 */
export const RejectedPinLabelContext = createContext<
  ((comment: BaseTicketComment, kind: RejectedPinKind) => string) | null
>(null);

/**
 * The reject a pin shows, or null. A reject made in this review wins over an
 * earlier one: it is the newer decision, and Restore undoes only it.
 */
export function rejectedPinKind(
  comment: BaseTicketComment,
): RejectedPinKind | null {
  if (comment.liked === false) return "here";
  // why: approving here is her newest word on the finding, as the card files it under Approved; a gray pin would contradict it
  if (comment.liked === true) return null;
  // why: the pins take the v1 comment type, which has no receipt field; the
  // Ask v2 viewer passes its review comments through with it intact. The
  // earlier comment's own reject counts too, as it does for the card.
  const { previouslyRejected, earlierComment, restoredAt } = comment as {
    previouslyRejected?: unknown;
    earlierComment?: { disposition?: string } | null;
    restoredAt?: string | null;
  };
  // Restored in this review: a live finding again, so its pin is not gray.
  if (restoredAt != null) return null;
  return previouslyRejected != null ||
    earlierComment?.disposition === "rejected"
    ? "before"
    : null;
}
